'use client';

/**
 * Top-level API methods — package purchase.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L5048-L5264).
 *
 * These methods live directly on the `api` object (not inside a nested
 * namespace), between the `payout` and `landing` namespaces. They are
 * exported as `packageMethods` and spread into the `api` object
 * construction in `../index.ts`.
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const packageMethods = {
// Bug 7 (SPEC-049): purchase package with internal wallet balance. The
// backend already exposes POST /packages/purchase/with-balance — this
// method gives the FE a typed entry point so the packages page can call
// it directly (and so we can wire the wallet 2FA dialog flow before
// this call without bloating the page).
// SPEC-068 / ADR-037: optional `roleAtPurchase` stamps the payout system
// to use for THIS sale (§3.7 invariante de independencia — once set, the
// deposit's `roleAtPurchase` is immutable). Defaults to 'networker' on
// the backend when omitted (R-04 — default for existing users).
//
// Sprint 95 QA fix (P-P0-3): optional `idempotencyKey` (second arg). When
// provided, the request sends an `Idempotency-Key` HTTP header (Stripe-
// style). The backend's idempotencyMiddleware caches the response 24h on
// Redis → retries with the same key return the cached response (no double-
// debit). The service-level stable depositId (`balance-${userId}-${key}`)
// is the second layer of protection if the Redis cache misses. The
// `options` arg is OPTIONAL → backward compatible (P-035: no API contract
// drift — existing callers without `options` work unchanged).
async purchaseWithBalance(
  data: {
    packageTier: string;
    roleAtPurchase?: 'networker' | 'cliente';
    // SPEC-088 §4.3 — must-read acceptance flag. Sent `true` by the
    // packages page (the modal flow guarantees the user accepted). The
    // backend Zod-validated schema at /api/packages/purchase/with-balance
    // checks `=== true` (defense-in-depth).
    mustReadAccepted?: boolean;
  },
  options?: { idempotencyKey?: string },
): Promise<{
  status: 'completed' | 'amount_mismatch' | 'already_processed';
  package?: string;
  depositId?: string;
  activatedAt?: string;
  expiresAt?: string;
  previousPackage?: string;
}> {
  return apiFetch<{
    status: 'completed' | 'amount_mismatch' | 'already_processed';
    package?: string;
    depositId?: string;
    activatedAt?: string;
    expiresAt?: string;
    previousPackage?: string;
  }>('/packages/purchase/with-balance', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: options?.idempotencyKey
      ? { 'Idempotency-Key': options.idempotencyKey }
      : undefined,
  });
},
// Bug 7 (SPEC-049): wallet signature 2FA flow.
//   1. `requestWalletNonce` → backend returns `{ message, nonce, expiresAt }`
//   2. user signs `message` with MetaMask via `personal_sign`
//   3. `verifyWalletSignature` → backend verifies, sets the
//      `2fa-recent:{userId}` Redis key (TTL 5min), returns `{ verified: true }`
//   4. `purchaseWithBalance` (above) succeeds because the
//      `requireRecent2FA` middleware now accepts the request.
async requestWalletNonce(payload: {
  intent: 'purchase_with_balance';
  tier?: string;
  amount?: string;
}): Promise<{ message: string; nonce: string; expiresAt: number }> {
  return apiFetch<{ message: string; nonce: string; expiresAt: number }>(
    '/auth/wallet-nonce',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
},
async verifyWalletSignature(payload: {
  signature: string;
  nonce: string;
  address: string;
}): Promise<{ verified: true }> {
  return apiFetch<{ verified: true }>('/auth/verify-wallet-signature', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
},
// ─── Bug 4 (SPEC-049): Public conference room access (3 endpoints) ──────
//
// Backend canonical source: `backend/src/controllers/public.controller.ts`.
// These endpoints are UNAUTHENTICATED (no JWT, no refresh-token cookie
// needed) — they're consumed by the public `/sala/[roomId]` route so
// guests can join a conference WITHOUT registering. The founder's
// intent (SPEC §1.4): "cualquier persona con el link pueda unirse a la
// conferencia SIN tener que registrarse (es el propósito de dar
// conferencias al público)."
//
// Anti-§6 (CM-19/CM-15): all three methods use `auth: false` so the
// existing `apiFetch` transport does NOT attach a `Bearer` header (the
// `auth` flag at line ~717 gates the Authorization header). This is the
// SAME pattern as `api.register`, `api.login`, `api.forgotPassword`,
// `api.verifyEmail`, etc. — no new `publicFetch` variant needed.
//
// `skipRefresh: true` is also set so a 401 response (e.g., wrong
// password on /join) does NOT trigger the transparent 401-refresh path
// (which would be a no-op for an unauthenticated guest + would dispatch
// `profileia:session-expired`, which would log the guest out of nothing
// — pointless). Guests see the 401 directly + can retry with the right
// password.
/** GET /public/rooms/:roomId — public room info (NO auth).
 *
 *  Bug 4 (SPEC-049): returns the public-safe room info shape. The
 *  backend distinguishes 3 error cases:
 *    404 ROOM_NOT_FOUND       — roomId doesn't exist in DB.
 *    410 ROOM_CLOSED          — conference ended (host closed / Gratuito 30-min limit).
 *    403 ROOM_ACCESS_DENIED   — invitation-only room (interview) — guests can't join.
 *  The caller (the `/sala/[roomId]` page) reads `err.code` to render
 *  distinct UIs (matches the auth-gated `/app/conferences/[roomId]`
 *  Bug 3 pattern). */
async getPublicRoom(roomId: string, signal?: AbortSignal): Promise<PublicRoomInfo> {
  return apiFetch<PublicRoomInfo>(
    `/public/rooms/${encodeURIComponent(roomId)}`,
    { method: 'GET', auth: false, skipRefresh: true, signal },
  );
},
/** GET /conferences/by-slug/:slug — public room lookup by slug (NO auth).
 *
 *  F5-002 fix (FASE 5 LAUNCH BLOCKER — SPEC-051 §2 F5-002):
 *  Pre-fix: the `/sala/[roomId]` page called `getPublicRoom(roomId)`
 *  with the slug as if it were a roomId. The backend's `validateRoomId`
 *  rejected the slug → 400 "ID de sala no válido" → visitor saw
 *  "No disponible — Ocurrió un error al cargar la sala."
 *
 *  Post-fix: the `/sala/[slug]` page calls this NEW method with the
 *  slug from the URL. The backend returns the public-safe room info
 *  (same shape as `getPublicRoom` + extra fields: `scheduledAt`,
 *  `maxSpectators`, `maxPresenters`, `durationLimitMinutes`,
 *  `requiresPassword`). The actual `roomId` is in the response so the
 *  caller can subsequently call `joinPublicRoom(roomId, ...)` to mint
 *  a guest session.
 *
 *  Error codes (same as `getPublicRoom`):
 *    400 BAD_REQUEST         — slug format invalid (alphanumeric + dashes only).
 *    404 NOT_FOUND           — slug doesn't exist in DB.
 *    410 ROOM_CLOSED         — conference ended.
 *    403 ROOM_ACCESS_DENIED  — invitation-only room (interview).
 *  No auth required (public endpoint). */
async getRoomBySlug(slug: string, signal?: AbortSignal): Promise<PublicRoomBySlugInfo> {
  return apiFetch<PublicRoomBySlugInfo>(
    `/conferences/by-slug/${encodeURIComponent(slug)}`,
    { method: 'GET', auth: false, skipRefresh: true, signal },
  );
},
/** POST /public/rooms/:roomId/join — guest join (NO auth).
 *
 *  Bug 4 (SPEC-049): mints a `guestSessionId` (UUID v4, 4h Redis TTL)
 *  that the frontend stores in `sessionStorage` so the
 *  `useConference.connect()` hook can find it on the WS handshake.
 *  Body:
 *    - displayName?: string — optional, defaults to 'Invitado' on the
 *      backend (chat surfaces this in messages; truncated to 50 chars
 *      to prevent layout breakage).
 *    - password?: string — REQUIRED for `access: 'password'` rooms
 *      (bcrypt-validated server-side). Omit for `access: 'public'`
 *      rooms.
 *    - invitationToken?: string — REQUIRED for `access: 'invitation'`
 *      rooms (interview rooms — SPEC-NEGOCIO §1.2). BUG-1a fix: the
 *      public route now validates the invitation token via
 *      `interviewRoomService.joinInterview(roomId, 'guest:<sid>',
 *      invitationToken)` — reuses the existing interview service
 *      (anti-P-382: NO parallel guest mechanism). The token is one-time
 *      use; the host must mint a new invitation per additional guest.
 *  Error codes:
 *    404 ROOM_NOT_FOUND, 410 ROOM_CLOSED, 403 ROOM_ACCESS_DENIED
 *    (same as getPublicRoom) +
 *    401 ROOM_WRONG_PASSWORD  — password room + missing/wrong password.
 *    403 ROOM_ACCESS_DENIED   — invitation room + missing/expired/used
 *                              invitation token.
 *    503 ROOM_FULL_GUESTS     — anonymous guest cap reached
 *                              (`env.conference.maxGuestsPerRoom`, default 100)
 *                              OR interview room capacity (maxPresenters=4)
 *                              reached.
 *    503 GUEST_SESSION_STORE_FAILED — Redis unavailable.
 *  Returns `{ guestSessionId, wsUrl: null, room: PublicRoomInfo }`.
 *  `wsUrl` is null so the frontend uses its default relative
 *  `/socket.io/` path (canonical pattern for same-origin Socket.IO
 *  behind a reverse proxy — no env var invented). */
async joinPublicRoom(
  roomId: string,
  body: { displayName?: string; password?: string; invitationToken?: string },
): Promise<{ guestSessionId: string; room: PublicRoomInfo; wsUrl: string | null }> {
  return apiFetch<{ guestSessionId: string; room: PublicRoomInfo; wsUrl: string | null }>(
    `/public/rooms/${encodeURIComponent(roomId)}/join`,
    {
      method: 'POST',
      auth: false,
      skipRefresh: true,
      body: JSON.stringify(body),
    },
  );
},
/** POST /public/rooms/:roomId/leave — guest leave (NO auth).
 *
 *  Bug 4 (SPEC-049): cleans up the guest session in Redis + decrements
 *  the per-room guest counter. Idempotent — silently succeeds for
 *  non-existent sessions (no info leak about whether the session was
 *  valid, expired, or already left). The frontend calls this on
 *  `handleLeave()` from the `/sala/[roomId]` page (best-effort — even
 *  if the network call fails, the frontend clears the sessionStorage
 *  + redirects). */
async leavePublicRoom(
  roomId: string,
  body: { guestSessionId: string },
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/public/rooms/${encodeURIComponent(roomId)}/leave`,
    {
      method: 'POST',
      auth: false,
      skipRefresh: true,
      body: JSON.stringify(body),
    },
  );
},
};

